import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaCheckCircle, 
  FaArrowRight, 
  FaClipboardCheck, 
  FaBalanceScale
} from 'react-icons/fa';
import { usePageContent } from '../services/pageContentService';
import HtmlContent from '../components/HtmlContent';
import RawHtmlPage, { isRawHtmlPage } from '../components/RawHtmlPage';

const PeerReviewPage = () => {
  const location = useLocation();
  const { content, loading } = usePageContent('peer_review');

  // Smooth scroll to anchor if hash is present in URL
  useEffect(() => {
    if (location.hash) {
      const hashId = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const el = document.getElementById(hashId);
        if (el) {
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 140;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - (headerHeight + 20);
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const titles = content?.titles || {};

  const defaultStages = [
    {
      step: '01',
      title: 'Initial Editorial Screening & Double-Blind Setup',
      timeframe: 'Day 1 – 5',
      desc: 'The Editor-in-Chief and Managing Editor examine the manuscript for scope, basic formatting, and originality (plagiarism check). Author identities are strictly blinded before sending to referees.'
    },
    {
      step: '02',
      title: 'Reviewer Assignment & Timeline',
      timeframe: 'Day 6 – 10',
      desc: 'A minimum of two independent, external subject-matter experts with demonstrated expertise in homoeopathy or clinical research are invited to evaluate the blinded paper.'
    },
    {
      step: '03',
      title: 'Independent Evaluation Period',
      timeframe: 'Day 11 – 25',
      desc: 'Reviewers critically assess the study design, methodology, homoeopathic prescribing rationale, statistical analysis, clinical implications, and clarity against international standards.'
    },
    {
      step: '04',
      title: 'Editorial Decision & Recommendation',
      timeframe: 'Day 26 – 35',
      desc: 'Based on reviewer evaluations, the editor reaches one of four decisions: (1) Accept; (2) Minor revisions; (3) Major revisions; (4) Reject. Anonymous feedback is shared with the corresponding author.'
    },
    {
      step: '05',
      title: 'Final Acceptance & Publication',
      timeframe: 'Day 36 – 45',
      desc: 'Upon satisfactory completion of requested revisions, the Editor-in-Chief issues formal acceptance followed by proof composition, DOI assignment, and Open Access publication.'
    }
  ];

  const stages = (Array.isArray(content?.stages) && content.stages.length > 0)
    ? content.stages
    : defaultStages;

  // Wait for the saved text rather than flashing the built-in default first
  if (loading) return <div className="min-h-screen" />;

  if (isRawHtmlPage(content)) {
    return <RawHtmlPage html={content.full_content} />;
  }

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">
      {/* Breadcrumb Bar */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-semibold text-gray-700">Peer Review Process</span>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <main className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm space-y-10">
          <div>
              <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
                QUALITY ASSURANCE & PEER EVALUATION
              </span>
              <h1 className="page-title">
                {titles.page_title || content?.page_title || 'Peer Review Process'}
              </h1>
              {content?.overview && (
                <div className="mt-4 text-sm sm:text-base text-gray-600 leading-relaxed border-l-4 border-[#03478E] pl-4 bg-blue-50/40 py-2 rounded-r-xl">
                  <HtmlContent content={content.overview} />
                </div>
              )}
            </div>

            {/* ================= FULL PAGE TEXT (IF ENTERED AS SINGLE DOCUMENT) ================= */}
            {content?.full_content ? (
              <div className="prose max-w-none text-gray-800 leading-relaxed space-y-6 border-t border-gray-100 pt-6">
                <HtmlContent content={content.full_content} />
              </div>
            ) : (
              /* ================= COHESIVE SINGLE PAGE SECTIONS (ALL OPEN) ================= */
              <div className="space-y-10">
                
                {/* 7.1 Peer Review Process & Stages */}
                <section
                  id="peer-review"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-6 pt-2 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.peer_review || 'Peer Review Process & Stages'}
                    </h2>
                  </div>

                  <p className="text-sm text-gray-600 leading-relaxed">
                    All manuscripts submitted to AJHM undergo double-blind peer review where both authors and reviewers remain anonymous to each other.
                  </p>

                  <div className="space-y-4">
                    {stages.map((stage, idx) => (
                      <div key={idx} className="bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100 space-y-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap pb-1">
                          <h3 className="font-bold text-sm sm:text-base text-[#0B3B60]">
                            {(stage.title || '').replace(/^[\d.]+\s*/, '')}
                          </h3>
                          {stage.timeframe && (
                            <span className="text-xs font-semibold text-[#03478E] bg-blue-50 px-3 py-1 rounded-full">
                              {stage.timeframe}
                            </span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                          {stage.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* 7.2 Revision Process */}
                <section
                  id="revision"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.revision || 'Revision Process'}
                    </h2>
                  </div>

                  <HtmlContent
                    content={content?.revision_text}
                    fallback="When revisions are requested, authors are provided with an itemized editorial letter and detailed, anonymous reviewer reports."
                    className="text-sm sm:text-base text-gray-700 leading-relaxed"
                  />

                  {Array.isArray(content?.revision_bullets) && content.revision_bullets.length > 0 && (
                    <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                      {content.revision_bullets.map((b, bIdx) => (
                        <li key={bIdx} className="flex items-start gap-2.5 bg-[#FAFBFD] p-3.5 rounded-xl border border-gray-100">
                          <FaCheckCircle className="text-[#03478E] text-sm mt-0.5 flex-shrink-0" />
                          <HtmlContent content={b} as="span" className="flex-1" />
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {/* 7.3 Confidentiality Policy */}
                <section
                  id="confidentiality"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.confidentiality || 'Confidentiality Policy'}
                    </h2>
                  </div>

                  <HtmlContent
                    content={content?.confidentiality_text}
                    fallback="Editors and reviewers treat all submitted manuscripts as privileged, strictly confidential material. Reviewers may not share manuscripts, use data or findings prior to publication, or disclose their peer evaluation without express written authorization from the Editor-in-Chief."
                    className="text-sm sm:text-base text-gray-700 leading-relaxed bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                  />
                </section>

                {/* 7.4 Reviewer Conflicts of Interest */}
                <section
                  id="conflicts"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.conflicts || 'Reviewer Conflicts of Interest'}
                    </h2>
                  </div>

                  <HtmlContent
                    content={content?.conflicts_text}
                    fallback="Invited reviewers who have recent collaborative, institutional, financial, or competitive relationships with the authors must decline the evaluation invitation immediately. Reviewers must notify the editorial office whenever any circumstance might reasonably be seen to bias their judgment."
                    className="text-sm sm:text-base text-gray-700 leading-relaxed bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                  />
                </section>

                {/* 7.5 Editorial Independence */}
                <section
                  id="independence"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.independence || 'Editorial Independence'}
                    </h2>
                  </div>

                  <HtmlContent
                    content={content?.independence_text}
                    fallback="Editorial decisions are made strictly on scientific validity, originality, and medical importance, completely independent of commercial interests, APC payment status, or the publisher."
                    className="text-sm sm:text-base text-gray-700 leading-relaxed bg-blue-50/50 p-5 rounded-2xl border border-blue-100 font-sans"
                  />
                </section>

              </div>
            )}

            {/* Actions (Single Page Flow) */}
            <div className="border-t border-gray-100 pt-8 flex flex-wrap items-center justify-end gap-3">
              <Link
                to="/upload"
                className="inline-flex items-center gap-2 py-2.5 px-5 bg-[#03478E] hover:bg-[#023366] text-white text-xs font-bold rounded-full uppercase tracking-wider transition-all shadow-xs"
              >
                <span>Submit Manuscript</span>
                <FaArrowRight className="text-[10px]" />
              </Link>
              <Link
                to="/publication-ethics"
                className="inline-flex items-center gap-2 py-2.5 px-4 bg-white border border-gray-200 text-[#03478E] hover:bg-gray-50 text-xs font-bold rounded-full uppercase tracking-wider transition-all"
              >
                <FaBalanceScale className="text-xs" />
                <span>Ethics Policy</span>
              </Link>
              <Link
                to="/information-for-authors"
                className="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-full uppercase tracking-wider transition-all"
              >
                <FaClipboardCheck className="text-xs" />
                <span>Author Guidelines</span>
              </Link>
            </div>

          </main>
        </div>
      </div>
    );
  };

export default PeerReviewPage;
